import {
  type AuthCredentials,
  type Episode,
  EpisodesArraySchema,
  type FollowedShow,
  FollowedShowsArraySchema,
  type Show,
  ShowSchema,
  type UserEpisode,
  UserEpisodesArraySchema,
} from '../types/schemas'

const PUBLIC_API_BASE = 'https://api.tvmaze.com'
const USER_API_BASE = 'https://api.tvmaze.com/v1'

class ApiService {
  private credentials: AuthCredentials | null = null

  constructor() {
    const stored = localStorage.getItem('tvmaze_auth')
    if (stored) {
      try {
        this.credentials = JSON.parse(stored)
      } catch {
        console.error('Failed to parse stored credentials')
      }
    }
  }

  setCredentials(credentials: AuthCredentials) {
    this.credentials = credentials
    localStorage.setItem('tvmaze_auth', JSON.stringify(credentials))
  }

  clearCredentials() {
    this.credentials = null
    localStorage.removeItem('tvmaze_auth')
  }

  getCredentials(): AuthCredentials | null {
    return this.credentials
  }

  private async fetchWithAuth(url: string, options: RequestInit = {}) {
    if (!this.credentials) {
      throw new Error('Not authenticated')
    }

    const authString = btoa(`${this.credentials.username}:${this.credentials.apiKey}`)

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Basic ${authString}`,
      },
    })

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`)
    }

    return response.json()
  }

  private async fetchPublic(url: string) {
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`)
    }

    return response.json()
  }

  // Public API methods
  async getShow(id: number): Promise<Show> {
    const data = await this.fetchPublic(`${PUBLIC_API_BASE}/shows/${id}`)
    return ShowSchema.parse(data)
  }

  async getShowEpisodes(showId: number): Promise<Episode[]> {
    const data = await this.fetchPublic(`${PUBLIC_API_BASE}/shows/${showId}/episodes`)
    return EpisodesArraySchema.parse(data)
  }

  // User API methods
  async getFollowedShows(): Promise<FollowedShow[]> {
    try {
      const data = await this.fetchWithAuth(`${USER_API_BASE}/user/follows/shows?embed=show`)
      return FollowedShowsArraySchema.parse(data)
    } catch (error) {
      console.error('Error fetching followed shows:', error)
      throw error
    }
  }

  async getMarkedEpisodes(showId?: number): Promise<UserEpisode[]> {
    const url = showId
      ? `${USER_API_BASE}/scrobble/shows/${showId}`
      : `${USER_API_BASE}/user/episodes`
    const data = await this.fetchWithAuth(url)
    return UserEpisodesArraySchema.parse(data)
  }

  async markEpisode(episodeId: number, type: 0 | 1 | 2 = 0): Promise<void> {
    await this.fetchWithAuth(`${USER_API_BASE}/scrobble/episodes/${episodeId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        marked_at: Math.floor(Date.now() / 1000),
        type,
      }),
    })
  }

  async unmarkEpisode(episodeId: number): Promise<void> {
    await this.fetchWithAuth(`${USER_API_BASE}/user/episodes/${episodeId}`, {
      method: 'DELETE',
    })
  }

  async followShow(showId: number): Promise<void> {
    await this.fetchWithAuth(`${USER_API_BASE}/user/follows/shows/${showId}`, {
      method: 'PUT',
    })
  }

  async unfollowShow(showId: number): Promise<void> {
    await this.fetchWithAuth(`${USER_API_BASE}/user/follows/shows/${showId}`, {
      method: 'DELETE',
    })
  }
}

export const apiService = new ApiService()
